/**
 * Handler: generate_lead event
 *
 * CDN fires generate_lead via event listeners in f-form-listener.js that trigger
 * each platform's internal handler (sendMultipleDests for GA4, dataLayer.push for GTM,
 * send_SSEventData for SS). No global CDN functions exist — all fallbacks are direct.
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';
import { generateEventId, gtmMarketFields } from '../utils';
import { sendGA4Fallback } from '../fallbacks/ga4';

export function handleGenerateLead(
  analyzify: WindowAnalyzify,
  formData: Record<string, any>
): void {
  const eventId = generateEventId();
  const props = analyzify.properties;
  const win = window as any;

  const leadSource = formData.source || 'form_submission';
  const formMethod = formData.method || 'submit';

  // GA4 - CDN fires via sendMultipleDests with generate_lead event
  sendGA4Fallback(analyzify, 'generate_lead', {
    form_method: formMethod,
    lead_source: leadSource,
    form_source: formData.form_source || leadSource,
    event_id: eventId,
  });

  // GTM - CDN pushes ee_generate_lead to dataLayer
  if (Array.isArray(win.dataLayer) && props?.GTM?.status && props.GTM.generate_lead) {
    win.dataLayer.push({
      event: 'ee_generate_lead',
      ss_event_id: eventId,
      lead_source: leadSource,
      form_method: formMethod,
      form_source: formData.form_source || leadSource,
      cart_id: analyzify.cart_id || null,
      event_id: eventId,
      analyzify_source: 'advanced',
      implementation_type: 'extension',
      ...gtmMarketFields(analyzify),
    });
  }

  // Facebook - call fbq() directly (no CDN function exists)
  if (
    typeof win.fbq === 'function' &&
    props?.FACEBOOK?.status &&
    (props.FACEBOOK.primary?.events?.generate_lead || props.FACEBOOK.secondary?.events?.generate_lead)
  ) {
    win.fbq('track', 'Lead', {
      content_name: leadSource,
    }, {
      eventID: eventId,
    });
  }

  // TikTok - call ttq.track() directly (no CDN function exists)
  const ttq = win.ttq;
  if (ttq && typeof ttq.track === 'function' && props?.TIKTOK?.status && props.TIKTOK.events?.generate_lead) {
    try {
      ttq.track('Lead', {
        content_name: leadSource,
      }, {
        event_id: eventId,
      });
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      analyzify.log?.(msg, 'dispatcher', 'ttq.generate_lead');
    }
  }

  // Server-side - CDN fires via send_SSEventData
  if (typeof (analyzify as any).ssGenerateLead === 'function' && props?.SERVERSIDE?.status) {
    try {
      (analyzify as any).ssGenerateLead?.({ leadSource, formMethod, eventId });
    } catch {
      // SS function not available — expected in headless
    }
  }

  analyzify.log('Generate lead tracked', 'dispatcher');
}
